import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Button } from "../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogOverlay,
} from "../components/ui/dialog";
import { useAuth } from "../hooks/use-auth";
import {
  LogOut,
  User,
  CreditCard,
  Settings,

  Download,
  Share,
  SquarePlus,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { cn } from "../lib/utils";
import { isLimitedByPause, isPaused } from "../lib/subscription";
import { isBlockedForInactivity } from "../lib/inactivity";

export default function UserNav() {
  const { user, logout, tourStep, setTourStep } = useAuth();
  const navigate = useNavigate();
  const [isDownloadDialogOpen, setIsDownloadDialogOpen] = useState(false);
  const [isIosSheetOpen, setIsIosSheetOpen] = useState(false);

  if (!user) {
    return null;
  }

  const env = import.meta.env.VITE_ENV;
  const isMobile = env === "mobile";

  const userInitials = user.name
    .split(" ")
    .map((n) => n[0])
    .join("");

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className={cn(
              "relative h-10 w-10 rounded-full focus-visible:ring-0 hover:bg-transparent",
              tourStep === "profile_icon" && "animate-pulse-strong"
            )}
          >
            <Avatar className="h-10 w-10">
              <AvatarImage src={user.avatar} alt={user.name} />
              <AvatarFallback>{userInitials}</AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56" align="end" forceMount>
          <DropdownMenuLabel className="font-normal">
            <div className="flex flex-col space-y-1">
              <p className="text-sm font-medium leading-none">{user.name}</p>
              <p className="text-xs leading-none text-muted-foreground">
                {user.email}
              </p>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            {!isLimitedByPause(user) && !isBlockedForInactivity(user) && (
            <DropdownMenuItem
              onClick={() => {
                if (tourStep === "profile_icon") {
                  setTourStep("uredi_profil_button");
                }
                navigate("/profile");
              }}
            >
              <User className="mr-2 h-4 w-4" />
              <span>Profil</span>
            </DropdownMenuItem>
            )}

            <DropdownMenuItem onClick={() => navigate("/settings")}>
              <Settings className="mr-2 h-4 w-4" />
              <span>Postavke</span>
            </DropdownMenuItem>

            {/* Only show download app option if not in mobile environment */}
            {!isMobile && (
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  setIsDownloadDialogOpen(true);
                }}
              >
                <Download className="mr-2 h-4 w-4" />
                <span>Preuzmi aplikaciju</span>
              </DropdownMenuItem>
            )}

            {user.role === "user" && !isMobile && !isPaused(user) && (
              <DropdownMenuItem onClick={() => navigate("/subscribe")}>
                <CreditCard className="mr-2 h-4 w-4" />
                <span>Pretplata</span>
              </DropdownMenuItem>
            )}
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={logout}>
            <LogOut className="mr-2 h-4 w-4" />
            <span>Odjavi se</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Only show download dialogs if not in mobile environment */}
      {!isMobile && (
        <>
          <Dialog
            open={isDownloadDialogOpen}
            onOpenChange={setIsDownloadDialogOpen}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Preuzmi aplikaciju</DialogTitle>
                <DialogDescription>
                  Odaberi platformu koju koristiš.
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <Button
                    className="w-full bg-accent hover:bg-[#e73e7b]"
                    onClick={() => {
                      setIsDownloadDialogOpen(false);
                      setIsIosSheetOpen(true);
                    }}
                  >
                    iOS
                  </Button>
                  <p className="text-xs text-muted-foreground mt-2">
                    Za iPhone
                  </p>
                </div>
                <div>
                  <a href="/NemaNeide.apk" download>
                    <Button className="w-full bg-accent hover:bg-[#e73e7b]">
                      Android
                    </Button>
                  </a>
                  <p className="text-xs text-muted-foreground mt-2">
                    Za Samsung, Xaomi, Huawey, Onenote...
                  </p>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={isIosSheetOpen} onOpenChange={setIsIosSheetOpen}>
            <DialogOverlay className="bg-black/50" />
            <DialogContent className="w-full max-w-sm m-0 p-4 border-none bg-transparent shadow-none flex flex-col justify-end items-center text-center focus-visible:outline-none">
              <div className="bg-card/95 p-6 rounded-xl text-card-foreground">
                <DialogHeader>
                  <DialogTitle>Instalacija na iOS</DialogTitle>
                  <DialogDescription className="sr-only">
                    Slijedite ove korake kako biste dodali aplikaciju na svoj
                    početni zaslon.
                  </DialogDescription>
                </DialogHeader>
                <p className="text-lg font-medium">
                  Potrebna su samo dva dodira.
                </p>
                <p className="text-muted-foreground mt-2">
                  Dodirni{" "}
                  <span className="inline-block align-middle bg-white rounded-md p-0.5 mx-1">
                    <Share className="h-5 w-5 text-[#2664c7]" />
                  </span>{" "}
                  ikonu na dnu zaslona, zatim se spusti i pronađi{" "}
                  <span className="font-semibold inline-flex items-center gap-1 align-middle bg-white text-[#2664c7] rounded-md px-1.5 py-0.5 mx-0.5">
                    Dodaj na početni zaslon <SquarePlus className="h-4 w-4" />
                  </span>
                  .
                </p>
                <p className="text-muted-foreground mt-2">
                  Nakon toga klikni{" "}
                  <span className="font-semibold inline-block align-middle bg-white text-[#2664c7] rounded-md px-1.5 py-0.5 mx-0.5">
                    Dodaj
                  </span>{" "}
                  (gornji desni kut).
                </p>
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}
    </>
  );
}
